import { useCallback, useMemo } from "react";
import { generatePath } from "react-router";

import type { GoogleFreeBookingLinksSettingsInput } from "@/lib/gql/__generated__/graphql";
import { GoogleFreeBookingLinksConnectionStatus, LangCode } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { FeatureIslandHeader } from "@/components/features/_layouts";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import type { ButtonProps } from "@/components/ui/button/button";
import { Button } from "@/components/ui/button/button";

import { useUpdateGoogleFreeBookingLinksSettings } from "../../../api";
import { useGoogleFreeBookingLinksSettingsContext } from "../_context";

export const Header = () => {
	const { updateGoogleFreeBookingLinksSettings } = useUpdateGoogleFreeBookingLinksSettings();
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();

	const { form } = useGoogleFreeBookingLinksSettingsContext();
	const { getValues, watch, reset } = form;
	const hotelID = getValues("hotelID");
	const status = getValues("status");

	const { isDirty, isValid } = form.formState;

	const buttonVariant = useMemo<ButtonProps["variant"]>(
		() => (isDirty && isValid ? "default" : "disabled"),
		[isDirty, isValid],
	);
	const buttonText = useMemo(
		() =>
			status === GoogleFreeBookingLinksConnectionStatus.Connectable ||
			status === GoogleFreeBookingLinksConnectionStatus.NotConnected
				? "保存"
				: "申請",
		[status],
	);

	const handleClick = useCallback(async () => {
		showLoadingScreenCallback();
		const settings = watch();
		const input: GoogleFreeBookingLinksSettingsInput = {
			...settings,
			geoLocation: {
				lat: settings.geoLocation?.lat ?? 0,
				lon: settings.geoLocation?.lon ?? 0,
			},
		};

		const { data, errors } = await updateGoogleFreeBookingLinksSettings(input).finally(() =>
			hideLoadingScreenCallback(),
		);

		if (data && !errors) {
			reset(settings);

			if (settings.status === GoogleFreeBookingLinksConnectionStatus.UnderReviewByChillnn) {
				notifyUserEventInfo({
					title: "Google無料予約リンクの連携申請を送信しました",
					description: "CHILLNNでの審査が完了次第、メールにて通知いたします。",
				});
			} else {
				notifyUserEventInfo({
					title: "Google無料予約リンク設定を更新しました",
				});
			}
		}
	}, [hideLoadingScreenCallback, reset, showLoadingScreenCallback, updateGoogleFreeBookingLinksSettings, watch]);

	return (
		<FeatureIslandHeader
			title="Google 無料予約リンク設定"
			navigator={{
				default: generatePath(PATHS.SETTINGS.EXTERNAL_CONNECTION.GOOGLE_FREE_BOOKING_LINKS.DEFAULT, {
					hotelID,
				}),
				i18n: (langCode) => {
					return generatePath(PATHS.SETTINGS.EXTERNAL_CONNECTION.GOOGLE_FREE_BOOKING_LINKS.I18N, {
						hotelID,
						langCode,
					});
				},
			}}
			activeLangCode={LangCode.Default}
			rightChildren={
				<Button variant={buttonVariant} type="button" size="xl" onClick={handleClick}>
					{buttonText}
				</Button>
			}
		/>
	);
};
